import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import supportsPassiveEvents from '@insight/toolkit-utils/lib/events/supportsPassiveEvents';
import throttle from '@insight/toolkit-utils/lib/timing/throttle';

import Tooltip from '../Tooltip/Tooltip';
import { calculateTextByLength, calculateTextByWidth } from './helpers';

const passiveEvent = supportsPassiveEvents()
  ? { capture: false, passive: true }
  : false;

export default class TextEllipsis extends Component {
  state = {
    displayedText: this.props.children,
    hasHiddenText: false,
  };

  textNode = null;

  componentDidMount() {
    this.calculateHiddenText();
    if (this.props.width) {
      window.addEventListener('resize', this.handleResize, passiveEvent);
    }
  }

  componentDidUpdate({ length, width }) {
    if (length !== this.props.length || width !== this.props.width) {
      this.calculateHiddenText();
    }
  }

  componentWillUnmount() {
    if (this.props.width) {
      window.removeEventListener('resize', this.handleResize, passiveEvent);
    }
  }

  calculateHiddenText = () => {
    const newState = this.props.width
      ? calculateTextByWidth(this.textNode, this.props)
      : calculateTextByLength(this.props);
    this.setState(newState);
  };

  handleResize = throttle(() => {
    const newState = calculateTextByWidth(this.textNode, this.props);
    this.setState(newState);
  }, this.props.resizeThrottle);

  setTextNode = (node) => {
    this.textNode = node;
  };

  render() {
    const {
      children,
      className,
      length,
      resizeThrottle,
      tail,
      textElement: TextElement,
      tooltip,
      width,
      id,
      ...rest
    } = this.props;
    const { displayedText, hasHiddenText } = this.state;

    const classes = cn('c-text-ellipsis', className);

    const content = (
      <TextElement
        className={classes}
        ref={this.setTextNode}
        {...rest}
        data-testid="textEllipsis"
      >
        {this.props.dontApplyTrimLogic ? children : displayedText}
        {hasHiddenText && !this.props.dontApplyTrimLogic && <span>{tail}</span>}
        {hasHiddenText && !!tooltip && (
          <span className="u-hide-visually" id={id}>
            {children}
          </span>
        )}
      </TextElement>
    );

    if (hasHiddenText && !!tooltip) {
      return (
        <Tooltip
          id={id}
          content={children}
          {...tooltip}
          isInModal={this.props.isInModal}
        >
          {content}
        </Tooltip>
      );
    }

    return content;
  }
}

TextEllipsis.propTypes = {
  children: PropTypes.string,
  className: PropTypes.string,
  length: PropTypes.number,
  resizeThrottle: PropTypes.number,
  tail: PropTypes.string,
  textElement: PropTypes.string,
  dontApplyTrimLogic: PropTypes.bool,
  tooltip: PropTypes.shape({
    anchorClassName: PropTypes.string,
    className: PropTypes.string,
    position: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
    title: PropTypes.node,
  }),
  width: PropTypes.number,
};

TextEllipsis.defaultProps = {
  children: '',
  className: '',
  length: 100,
  resizeThrottle: 250,
  tail: '...',
  textElement: 'span',
};
